// src/contexts/administrativo/ConfigDFeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';
import {
    adicionarConfigDFe,
    alterarConfigDFe,
    excluirConfigDFe,
    listarConfigDFe,
    salvarCertificadoConfigDFe as salvarCertificadoConfigDFeApi,
} from '../../services/administrativo/ConfigDFeService';

const ConfigDFeContext = createContext();

export const useConfigDFe = () => useContext(ConfigDFeContext);

export const ConfigDFeProvider = ({ children }) => {
    const [configDFeList, setConfigDFeList] = useState([]);
    const { showAlert } = useAlert(); // Access showAlert from AlertContext
    const { showMessage } = useMessage();

    const verificarValidadeCertificado = (configs) => {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const configComAviso = configs
            .filter((config) => config.certificadoValidade)
            .map((config) => {
                const validade = new Date(config.certificadoValidade);
                validade.setHours(0, 0, 0, 0);
                const diasRestantes = Math.ceil((validade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

                return { ...config, diasRestantes };
            })
            .filter((config) => config.diasRestantes <= 10)
            .sort((a, b) => a.diasRestantes - b.diasRestantes)[0];

        if (!configComAviso) return;

        if (configComAviso.diasRestantes < 0) {
            showMessage('O certificado digital está vencido.', 'warning');
            return;
        }

        showMessage(`O certificado digital irá expirar em ${configComAviso.diasRestantes} dias.`, 'warning');
    };

    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                const result = await listarConfigDFe();
                setConfigDFeList(result);
                verificarValidadeCertificado(result);
            } catch (error) {
                console.error("Erro ao buscar configurações de DFe:", error);
            }
        };
        fetchDataFromAPI();
    }, []);

    const addConfigDFe = async (configDFe) => {
        try {
            const result = await adicionarConfigDFe(configDFe);
            setConfigDFeList((prev) => [...prev, result]);
            showAlert('Configuração de DFe adicionada com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao adicionar configuração de DFe:", error);
            showAlert('Erro ao adicionar configuração de DFe!', 'danger');
            throw error;
        }
    };

    const updateConfigDFe = async (configDFe) => {
        try {
            const result = await alterarConfigDFe(configDFe);
            setConfigDFeList((configDFeList) =>
                configDFeList.map((c) => (c.id === result.id ? result : c))
            );
            showAlert('Configuração de DFe alterada com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao atualizar configuração de DFe:", error);
            showAlert('Erro ao alterar configuração de DFe!', 'danger');
            throw error;
        }
    };

    const deleteConfigDFe = async (id) => {
        try {            
            await excluirConfigDFe(id);
            setConfigDFeList((prev) => prev.filter(c => c.id !== id));
            showAlert('Configuração de DFe excluída com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir configuração de DFe:", error);
            showAlert('Erro ao excluir configuração de DFe!', 'danger');
            throw error;
        }
    };

    const salvarCertificadoConfigDFe = async (configDFeId, arquivo, senha) => {
        try {
            const result = await salvarCertificadoConfigDFeApi(configDFeId, arquivo, senha);
            setConfigDFeList((configDFeList) =>
                configDFeList.map((c) => (c.id === result.id ? result : c))
            );
            showAlert('Certificado digital salvo com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao salvar certificado digital:", error);
            showAlert(error?.message || 'Erro ao salvar certificado digital!', 'danger');
            throw error;
        }
    };

    return (
        <ConfigDFeContext.Provider value={{ configDFeList, addConfigDFe, updateConfigDFe, deleteConfigDFe, salvarCertificadoConfigDFe }}>
            {children}
        </ConfigDFeContext.Provider>
    );
};
